/**
 * Cross-platform alert utility.
 *
 * React Native's Alert.alert() is a no-op on web. This module uses the native
 * Alert on iOS/Android; on web, `showAlert` is `window.alert()` and
 * `showConfirm` is an in-app dialog (see stores/confirmDialogStore.ts for why
 * it is not `window.confirm`).
 */

import { Platform, Alert } from "react-native";
import { useConfirmDialogStore } from "../stores/confirmDialogStore";

/**
 * Show an informational alert (no user decision needed).
 * On web: window.alert(). On native: Alert.alert().
 */
export function showAlert(
  title: string,
  message?: string,
  onDismiss?: () => void
): void {
  if (Platform.OS === "web") {
    window.alert(message ? `${title}\n\n${message}` : title);
    onDismiss?.();
  } else {
    Alert.alert(
      title,
      message,
      onDismiss ? [{ text: "OK", onPress: onDismiss }] : undefined
    );
  }
}

/**
 * Show a confirmation dialog (needs user response).
 * On web: an in-app dialog rendered by ConfirmDialogHost. On native:
 * Alert.alert() with Cancel/Confirm buttons.
 */
export function showConfirm(
  title: string,
  message: string,
  onConfirm: () => void,
  options?: {
    confirmText?: string;
    cancelText?: string;
    destructive?: boolean;
    onCancel?: () => void;
  }
): void {
  if (Platform.OS === "web") {
    // Not window.confirm — Safari can be told to suppress native dialogs, after
    // which every confirm returns false and the action silently never runs.
    void useConfirmDialogStore
      .getState()
      .ask({
        title,
        message,
        confirmText: options?.confirmText ?? "OK",
        cancelText: options?.cancelText ?? "Cancel",
        destructive: options?.destructive ?? false,
      })
      .then((confirmed) => {
        if (confirmed) onConfirm();
        else options?.onCancel?.();
      });
  } else {
    Alert.alert(title, message, [
      {
        text: options?.cancelText ?? "Cancel",
        style: "cancel",
        onPress: options?.onCancel,
      },
      {
        text: options?.confirmText ?? "OK",
        style: options?.destructive ? "destructive" : "default",
        onPress: onConfirm,
      },
    ]);
  }
}
